import React, { useEffect, lazy, Suspense, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Spin, message } from 'antd';

import {
  clearError,
  fetchInviteData,
  fetchProjectData,
  startAssessment,
} from '../../store/actions/ProjectActions';
import IDEHeader from './IDEHeader';
import * as actions from '../../store/actions/AssessmentActions';
import ProjectCompleted from './ProjectCompleted';
import { PROJECT_PAGE, PROJECT_STATUS } from '../../const';
import styles from './ProjectContainer.scss';

const Instruction = lazy(() => import('./Instructions/Instruction'));
const ProjectMediaPermission = lazy(() => import('./ProjectMediaPermission'));
const ProjectExpired = lazy(() => import('./ProjectExpired'));
const ProjectIviteContainer = lazy(() => import('./ProjectIviteContainer'));

const ProjectContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { site } = useParams();

  // Redux state
  const user_detail = useSelector(state => state.auth);
  const { assessment, session, invite, error } = useSelector(state => state.project);
  const { meeting } = useSelector(state => state.assesmentRecording);
  const { hasScreen } = useSelector(state => state.assesmentRecording);

  const onMediaSuccess = () => {
    if (invite?.assessmentId) {
      dispatch(startAssessment(invite.assessmentId));
    }
  };

  const isValidUser = user_detail?.email_id === invite?.email;
  const hasAssessment = assessment && Object.keys(assessment).length !== 0;
  const hasInviteData = invite && Object.keys(invite).length !== 0;

  /*
    wondering why I have added this use effect here? let me explain it
    first time when user land here we need the invite data to take decision so 
    making call to get the invite data from this use effect
  */
  useEffect(() => {
    const fetchData = async () => {
      const searchParams = new URLSearchParams(location.search);
      const inviteId = searchParams.get('invite_id');
      if (inviteId) {
        dispatch(fetchInviteData(inviteId));
      }
    };
    fetchData();
  }, []);

  /*
    I know this is one more logic we need a comment to understand what is going on here right?
    it is possible that user might be idle for quite a time which will expires the token and if refresh or make the call
    to the backend then a 403 or 401 error will come that we catch and and route him to authenticate here
  */
  useEffect(() => {
    if (error?.status == 403 || error?.status == 401) {
      const searchParams = new URLSearchParams(location.search);
      const inviteId = searchParams.get('invite_id');
      history.replace(`/project?invite_id=${inviteId}`);
      dispatch(clearError());
    }
  }, [error, history, location]);

  /*
  this useEffect is very important why? it is working as a routing based on the different condition we will 
  change the url which then render the component based on the page name like ide, instruction.... etc
*/
  useEffect(() => {
    if ((!isValidUser && hasInviteData) || error || !localStorage.getItem('access-token')) {
      const searchParams = new URLSearchParams(location.search);
      const inviteId = searchParams.get('invite_id');
      history.replace(`/project?invite_id=${inviteId}`);
      return;
    }
    if (Object.keys(assessment).length === 0 && invite?.assessmentId) {
      dispatch(fetchProjectData(invite.assessmentId));
    }

    if (assessment.status === PROJECT_STATUS.IN_PROGRESS) {
      if (hasScreen && meeting) {
        history.push(`/project/ide?invite_id=${invite?.id || ''}`);
      } else {
        history.push(`/project/media?invite_id=${invite?.id || ''}`);
      }
      return;
    }
    if (assessment.status === PROJECT_STATUS.PENDING) {
      if (!hasScreen && !meeting) {
        history.push(`/project/media?invite_id=${invite?.id || ''}`);
      }
    }
    if (assessment.status === PROJECT_STATUS.INVITED) {
      history.push(`/project/instruction?invite_id=${invite?.id || ''}`);
      return;
    }
    if (assessment.status === PROJECT_STATUS.SUBMITTED) {
      history.replace(`/project/submitted?invite_id=${invite?.id || ''}`);
      return;
    }
  }, [user_detail, assessment, history, invite, dispatch, error]);

  const withSuspense = (Component, props = {}) => (
    <Suspense
      fallback={
        <div className={styles['fallback-style']}>
          <Spin />
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  );

  const renderStep = useCallback(() => {
    if (hasInviteData && !isValidUser) {
      return withSuspense(ProjectIviteContainer, { assessment });
    }

    if (assessment.status === PROJECT_STATUS.SUBMITTED) {
      return withSuspense(ProjectCompleted);
    }

    if (assessment.status === PROJECT_STATUS.EXPIRED) {
      return withSuspense(ProjectExpired);
    }

    if (hasAssessment) {
      // if (isExpired(assessment?.expiresAt) || site === PROJECT_STATUS.EXPIRED) {
      //   return withSuspense(ProjectExpired);
      // }

      switch (site) {
        case PROJECT_PAGE.INSTRUCTION:
          return withSuspense(Instruction);

        case PROJECT_PAGE.MEDIA:
          return withSuspense(ProjectMediaPermission, {
            onMediaSuccess,
            assessmentId: invite.assessmentId,
          });

        case PROJECT_PAGE.IDE:
          return withSuspense(() => (
            <>
              <IDEHeader />
              <iframe
                src={session.sessionUrl}
                style={{
                  width: '100%',
                  height: '90.7vh',
                  border: 'none',
                  margin: 0,
                  padding: 0,
                  background: 'black',
                }}
                title="Assessment IDE"
                allow="clipboard-read; clipboard-write"
              />
            </>
          ));

        case PROJECT_PAGE.EXPIRED:
          return withSuspense(ProjectExpired, { type: 'invite_expired' });

        case PROJECT_PAGE.SUBMITTED:
          return withSuspense(ProjectCompleted);

        default:
          return null;
      }
    }
  }, [isValidUser, assessment, hasInviteData, hasAssessment, site, session]);

  return <div style={{ background: '#001e3c', minHeight: '100vh' }}>{renderStep()}</div>;
};

export default ProjectContainer;
